import { useState, useEffect, ReactNode, useMemo, useCallback, useRef } from "react";
import { useSearch } from "wouter";
import {
  useEvents,
  useCreateReservation,
  useProfile,
  useNeighborhoods,
  useReservation,
  useReservations,
  useAcceptBookingInvite,
} from "@/lib/api";
import { clearInviteCode, getInviteCode } from "@/lib/referral";
import tracking from "@/lib/tracking";
import { useToast } from "@/components/ux/Toast";
import { UserAPI } from "@shared/types";
import { BookingStep } from "@/components/booking/types";
import { BookingContext, BookingState } from "@/components/booking/getbookingContext";
import { useAppNavigation } from "@/lib/platform";
import { todayjs } from "@/utils/dateUtils";
import { BOOKING_CUTOFF_HOURS } from "@shared/constants";
import dayjs from "dayjs";
import { useT } from "@/lib/i18n";
import { useNoShowDisclaimer } from "@/lib/features";

export function BookingProvider({ children }: { children: ReactNode }) {
  const { navigate } = useAppNavigation();
  const searchParams = useSearch();
  const { data: profileData, refetch: refetchProfile } = useProfile();

  const { data: eventsData, isLoading: eventsLoading } = useEvents();
  const { data: reservationsData, refetch: refetchReservations } =
    useReservations();
  const createReservation = useCreateReservation();
  const toast = useToast();

  // Extract URL parameters
  const urlParams = new URLSearchParams(searchParams);
  const eventIdFromUrl = urlParams.get("eventId") || urlParams.get("eventTimeId");
  const bookingIdFromUrl = urlParams.get("bookingId");
  const neighborhoodIdFromUrl = urlParams.get("neighborhoodId");
  const inviteCodeFromUrl = urlParams.get("inviteCode");
  const isAutoNextMode = eventIdFromUrl === "next";
  const autoNextApplied = useRef(false);
  const neighborhoodPrePopulated = useRef(false);
  const inviteFinalizeAttempted = useRef(false);
  const acceptInvite = useAcceptBookingInvite();
  const { t } = useT();

  // Persist invite code from URL so deep-link → sign-in → /booking still
  // wires up correctly even if the user authenticates between the two.
  useEffect(() => {
    if (inviteCodeFromUrl) {
      // setInviteCode is in ../../lib/referral; tracking happens in InviteAccept.
      try {
        localStorage.setItem("outclose_invite_code", inviteCodeFromUrl.toUpperCase().trim());
      } catch {
        /* best-effort */
      }
    }
  }, [inviteCodeFromUrl]);

  // Booking flow state
  const [bookingStep, setBookingStep] = useState<BookingStep>(
    BookingStep.SELECTING
  );

  // Location selection state
  const [selectedCity, _setSelectedCity] = useState(
    urlParams.get("city") || profileData?.data?.user?.city
  );

  const [selectedNeighborhood, setSelectedNeighborhood] = useState<any>(null);
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);

  // Booking details state
  const [specialRequests, setSpecialRequests] =
    useState<UserAPI.SpecialRequests>(() => {
      // Load from localStorage on initialization
      try {
        const saved = localStorage.getItem("bookingSpecialRequests");
        return saved ? JSON.parse(saved) : {};
      } catch (error) {
        console.error(
          "Failed to load special requests from localStorage:",
          error
        );
        return {};
      }
    });

  const noShowDisclaimerShown = useNoShowDisclaimer();

  // Reservation data (full response)
  const [reservationData, setReservationData] =
    useState<UserAPI.CreateReservationResponseData | null>(null);

  // Fetch existing booking if editing
  const { data: existingBookingData, isLoading: isLoadingExistingBooking } =
    useReservation(editingBookingId, {
      enabled: !!editingBookingId,
      useBackoff: false,
    });

  // Poll pending booking status after payment with exponential backoff
  const { data: bookingStatusData } = useReservation(
    reservationData?.booking.id || null,
    {
      enabled:
        bookingStep === BookingStep.PROCESSING && !!reservationData?.booking.id,
      useBackoff: true, // Use exponential backoff: 500ms -> 1s -> 2s -> 4s -> 5s (max)
      refetchInterval: 1000,
    }
  );

  // selectedEventId is derived from URL ("next" is a special value for auto-select, not a real ID)
  const selectedEventId = isAutoNextMode ? "" : (eventIdFromUrl || "");
  const profile = profileData?.data?.profile;
  const allEventsRaw = useMemo(
    () => eventsData?.data || [],
    [eventsData?.data]
  );
  const reservations = useMemo(
    () => reservationsData?.data || [],
    [reservationsData?.data]
  );
  const existingBooking = existingBookingData?.success
    ? existingBookingData.data || null
    : null;

  // Fetch all neighborhoods for the selected city (for filtering events by city)
  const { data: cityNeighborhoodsData } =
    useNeighborhoods(undefined, selectedCity || undefined);
  const cityNeighborhoodIds = useMemo(
    () => new Set((cityNeighborhoodsData?.data || []).map((n: any) => n.id)),
    [cityNeighborhoodsData?.data]
  );

  // Fetch neighborhoods for the existing booking's event WITHOUT city filter
  // so we can discover the correct city for the booking's neighborhood
  const { data: editModeNeighborhoodsData } = useNeighborhoods(
    existingBooking?.eventTimeId || undefined
  );

  // Filter out events the user already booked, then by city availability
  const allEvents = useMemo(() => {
    const reservedEventIds = new Set(
      reservations
        .filter(
          (r: UserAPI.Reservation) =>
            r.status === "active" &&
            r.eventTimeId !== existingBooking?.eventTimeId
        )
        .map((r: UserAPI.Reservation) => r.eventTimeId)
    );

    let filtered = allEventsRaw.filter((event: any) => !reservedEventIds.has(event.id));

    if (selectedCity && cityNeighborhoodIds.size > 0) {
      filtered = filtered.filter((event: any) => {
        if (!event.neighborhoodIds) return true;
        return event.neighborhoodIds.some((id: string) => cityNeighborhoodIds.has(id));
      });
    }

    return filtered;
  }, [allEventsRaw, reservations, existingBooking?.eventTimeId, selectedCity, cityNeighborhoodIds]);

  const selectedEvent = selectedEventId
    ? allEventsRaw.find((e: any) => e.id === selectedEventId)
    : null;

  // Fetch neighborhoods for the selected event + city combination
  const { data: neighborhoodsData, isLoading: neighborhoodsLoading } =
    useNeighborhoods(
      (selectedEvent?.id ?? selectedEventId) || undefined,
      selectedCity || undefined
    );
  const neighborhoods = useMemo(
    () => neighborhoodsData?.data || [],
    [neighborhoodsData?.data]
  );

  // isLoading blocks the entire page; reserve it for the initial load only.
  // Neighborhood refetches (triggered by date/city changes) surface inline via
  // neighborhoodsLoading so the sections above don't get torn down.
  const isLoading = eventsLoading || isLoadingExistingBooking;

  const setSelectedCity = useCallback((city: string) => {
    _setSelectedCity(city);
    setSelectedNeighborhood((prev: any) =>
      prev?.cityId === city ? prev : null
    );
    // Update URL param
    const newParams = new URLSearchParams(searchParams);
    if (city) {
      newParams.set("city", city);
    } else {
      newParams.delete("city");
    }
    navigate(`/booking?${newParams.toString()}`);
  }, [setSelectedNeighborhood, _setSelectedCity, searchParams, navigate]);
  // Pre-select user's city from profile
  useEffect(() => {
    if (profileData?.data?.user?.city && !selectedCity) {
      setSelectedCity(profileData?.data?.user?.city);
    }
  }, [profileData?.data?.user?.city, selectedCity, setSelectedCity]);

  // Clear neighborhood if it's no longer available for the current event/city
  useEffect(() => {
    if (selectedNeighborhood && neighborhoods.length > 0) {
      const isStillAvailable = neighborhoods.some(
        (n: any) => n.id === selectedNeighborhood.id
      );
      if (!isStillAvailable) {
        setSelectedNeighborhood(null);
      }
    }
  }, [neighborhoods, selectedNeighborhood]);

  // Pre-select neighborhood from URL (?neighborhoodId=...) once the neighborhood
  // list loads. Used by the +1 invite flow so the invitee lands prefilled.
  useEffect(() => {
    if (!neighborhoodIdFromUrl) return;
    if (selectedNeighborhood?.id === neighborhoodIdFromUrl) return;
    if (neighborhoods.length === 0) return;
    const match = neighborhoods.find((n: any) => n.id === neighborhoodIdFromUrl);
    if (match) setSelectedNeighborhood(match);
  }, [neighborhoodIdFromUrl, neighborhoods, selectedNeighborhood?.id]);

  // Auto-select neighborhood if only one is available
  useEffect(() => {
    if (neighborhoods.length === 1 && !selectedNeighborhood) {
      setSelectedNeighborhood(neighborhoods[0]);
    }
  }, [neighborhoods, selectedNeighborhood]);

  // Auto-select next available event when eventId=next
  useEffect(() => {
    if (!isAutoNextMode || autoNextApplied.current || allEvents.length === 0) return;

    const now = dayjs();
    const nextEvent = allEvents.find((event: any) =>
      todayjs(event.date, event.time).subtract(BOOKING_CUTOFF_HOURS, "hours").isAfter(now)
    );

    if (nextEvent) {
      autoNextApplied.current = true;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("eventId", nextEvent.id);
      navigate(`/booking?${newParams.toString()}`);
    }
  }, [isAutoNextMode, allEvents, searchParams, navigate]);

  // Set editing booking ID from URL
  useEffect(() => {
    if (bookingIdFromUrl && !editingBookingId) {
      setEditingBookingId(bookingIdFromUrl);
    }
  }, [bookingIdFromUrl, editingBookingId]);

  // Pre-populate eventId from existing booking (no neighborhood dependency)
  useEffect(() => {
    if (existingBooking && !selectedEventId && existingBooking.eventTimeId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("eventId", existingBooking.eventTimeId);
      if (bookingIdFromUrl) {
        newParams.set("bookingId", bookingIdFromUrl);
      }
      navigate(`/booking?${newParams.toString()}`);
    }
  }, [existingBooking, selectedEventId, searchParams, bookingIdFromUrl, navigate]);

  // Pre-populate city from existing booking's neighborhood
  // Uses the unfiltered neighborhoods fetch so we can find the city
  // even when the current selectedCity is wrong (e.g. user's profile city)
  useEffect(() => {
    if (existingBooking && editModeNeighborhoodsData?.data) {
      const bookingNeighborhood = editModeNeighborhoodsData.data.find(
        (n: any) => n.id === existingBooking.neighborhoodId
      );
      if (bookingNeighborhood?.cityId && bookingNeighborhood.cityId !== selectedCity) {
        setSelectedCity(bookingNeighborhood.cityId);
      }
    }
  }, [existingBooking, editModeNeighborhoodsData?.data, selectedCity, setSelectedCity]);

  // Pre-populate neighborhood from existing booking (only once on initial load)
  useEffect(() => {
    if (
      existingBooking &&
      neighborhoods.length > 0 &&
      !selectedNeighborhood &&
      existingBooking.neighborhoodId &&
      !neighborhoodPrePopulated.current
    ) {
      const neighborhood = neighborhoods.find(
        (n: any) => n.id === existingBooking.neighborhoodId
      );
      if (neighborhood) {
        setSelectedNeighborhood(neighborhood);
        neighborhoodPrePopulated.current = true;
      }
    }
  }, [existingBooking, neighborhoods, selectedNeighborhood]);
  useEffect(() => {
    if (existingBooking?.specialRequests) {
      setSpecialRequests(JSON.parse(existingBooking.specialRequests));
    }
  }, [existingBooking?.specialRequests, setSpecialRequests]);

  // Monitor booking status while polling
  useEffect(() => {
    if (
      bookingStep === BookingStep.PROCESSING &&
      bookingStatusData?.success &&
      bookingStatusData.data
    ) {
      const booking = bookingStatusData.data;

      if (booking.status === "active") {
        // Update reservation data with the completed booking
        if (reservationData) {
          setReservationData({
            ...reservationData,
            booking,
          });
        }
        setBookingStep(BookingStep.SUCCESS);
        refetchProfile();
        refetchReservations();
      } else if (booking.status === "cancelled") {
        toast.error("Booking was cancelled due to payment issue");
        setBookingStep(BookingStep.SELECTING);
        setReservationData(null);
      }
    }
  }, [
    bookingStatusData,
    bookingStep,
    toast,
    reservationData,
    refetchProfile,
    refetchReservations,
  ]);

  // Auto-finalize any stashed "+1" invite once a booking goes active. The
  // invite code can arrive via either the URL (?inviteCode=) or localStorage
  // (set on the /invite/:code preview). Either way we re-issue accept —
  // now that an active booking exists, the server links the two and marks
  // the invite accepted.
  useEffect(() => {
    if (inviteFinalizeAttempted.current) return;
    if (bookingStep !== BookingStep.SUCCESS) return;
    const activeBookingId = reservationData?.booking?.id;
    if (!activeBookingId) return;
    if (reservationData?.booking?.status !== "active") return;
    const code = inviteCodeFromUrl || getInviteCode();
    if (!code) return;
    inviteFinalizeAttempted.current = true;
    acceptInvite.mutate(code, {
      onSuccess: (res) => {
        if (res.success && res.data && !res.data.requiresBooking) {
          tracking.inviteAcceptResult(code, "accepted");
          clearInviteCode();
        } else {
          tracking.inviteAcceptResult(code, "error");
        }
      },
      onError: () => tracking.inviteAcceptResult(code, "error"),
    });
  }, [
    bookingStep,
    reservationData?.booking?.id,
    reservationData?.booking?.status,
    inviteCodeFromUrl,
    acceptInvite,
  ]);

  const handleCreateReservation = () => {
    if (!selectedNeighborhood) {
      toast.error("Please select a neighborhood");
      return;
    }
    let specialRequestsJson: string | undefined = undefined;

    if (Object.keys(specialRequests).length > 0) {
      specialRequestsJson = JSON.stringify(specialRequests);
      try {
        localStorage.setItem("bookingSpecialRequests", specialRequestsJson);
      } catch (error) {
        console.error(
          "Failed to save special requests to localStorage:",
          error
        );
      }
    }

    createReservation.mutate(
      {
        eventTimeId: selectedEventId,
        neighborhoodId: selectedNeighborhood.id,
        specialRequests: specialRequestsJson,
        replaceBookingId: editingBookingId || undefined,
        // Booking with the disclaimer on screen is the agreement.
        noShowDisclaimerAccepted: noShowDisclaimerShown,
      },
      {
        onSuccess: (response) => {
          if (response.data) {
            setReservationData(response.data);

            if (response.data.requiresPayment) {
              setBookingStep(BookingStep.PAYMENT);
            } else {
              setBookingStep(BookingStep.SUCCESS);
            }
          }
          refetchProfile();
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error || "Failed to create reservation";
          toast.error(errorMessage);
        },
      }
    );
  };

  const handlePaymentSuccess = () => {
    toast.success(t('payment.purchaseSuccess'));
    setBookingStep(BookingStep.PROCESSING);
  };

  const handlePaymentCancel = () => {
    setBookingStep(BookingStep.SELECTING);
    setReservationData(null);
  };

  const handleSuccessDashboard = () => {
    navigate("/dashboard");
  };

  const handleSuccessBookAnother = () => {
    setBookingStep(BookingStep.SELECTING);
    setReservationData(null);
    setSelectedNeighborhood(null);
    setSpecialRequests({});
    // Clear localStorage
    try {
      localStorage.removeItem("bookingSpecialRequests");
    } catch (error) {
      console.error(
        "Failed to clear special requests from localStorage:",
        error
      );
    }
    navigate("/booking");
  };

  const setSelectedEventId = (eventId: string) => {
    setSelectedNeighborhood(null);
    const newParams = new URLSearchParams(searchParams);
    if (eventId) {
      newParams.set("eventId", eventId);
    } else {
      newParams.delete("eventId");
    }
    // Keep bookingId if present
    if (bookingIdFromUrl) {
      newParams.set("bookingId", bookingIdFromUrl);
    }
    navigate(`/booking?${newParams.toString()}`);
  };

  const value: BookingState = {
    bookingStep,
    selectedCity,
    selectedNeighborhood,
    selectedEventId,
    specialRequests,
    reservationData,
    isEditMode: !!editingBookingId,
    existingBooking,
    profile,
    allEvents,
    neighborhoods,
    selectedEvent,
    isLoading,
    neighborhoodsLoading,
    setSelectedCity,
    setSelectedNeighborhood,
    setSelectedEventId,
    setSpecialRequests,
    noShowDisclaimerShown,
    handleCreateReservation,
    handlePaymentSuccess,
    handlePaymentCancel,
    handleSuccessDashboard,
    handleSuccessBookAnother,
    isCreatingReservation: createReservation.isPending,
  };

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
}
